/**
 * React i18n context — wraps the app in a locale + `t()` translate function.
 *
 * Mounted once in root.tsx's `App()`, around the whole `<Outlet />`, seeded with the
 * server-resolved locale (see locale.server.ts) so SSR and the first client render agree (no
 * hydration mismatch). Once mounted, it upgrades to the AUTHORITATIVE merchant admin-language
 * setting from App Bridge's Config API (`shopify.config.locale`) the instant that's available —
 * only text content changes at that point, never DOM structure, so this can't produce a React
 * hydration error the way changing markup shape post-mount would.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { DEFAULT_LOCALE, resolveLocale, translate, type Locale } from "./dictionary";

export type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

interface I18nContextValue {
  locale: Locale;
  t: TranslateFn;
}

const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  t: (key, params) => translate(DEFAULT_LOCALE, key, params),
});

export function I18nProvider({
  initialLocale,
  children,
}: {
  initialLocale: Locale;
  children: ReactNode;
}) {
  const [locale, setLocale] = useState<Locale>(initialLocale);

  useEffect(() => {
    const bridgeLocale = (
      window as unknown as { shopify?: { config?: { locale?: string } } }
    ).shopify?.config?.locale;
    if (!bridgeLocale) return;
    const resolved = resolveLocale(bridgeLocale);
    setLocale((prev) => (prev === resolved ? prev : resolved));
  }, []);

  const t = useCallback<TranslateFn>((key, params) => translate(locale, key, params), [locale]);
  const value = useMemo<I18nContextValue>(() => ({ locale, t }), [locale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** Full context value ({ locale, t }) — use when a component needs the locale itself. */
export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

/** Just the translate function — the common case for merchant-facing copy. */
export function useT(): TranslateFn {
  return useContext(I18nContext).t;
}
